import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  CASE_KEY,
  CASES_KEY,
  chooseForm,
  extractionKey,
  extractionPageUrl,
  noteStandWritten,
  takeOver,
  useCase,
  type ExtractionOut,
  type FormChoice,
  type ReadingReviewOut,
  type Resolution,
} from "@/api";
import { StateMessage } from "@/components/StateMessage";
import { originalOf, otherPageHref, readFromLabel } from "@/lib/otherPage";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import { FormUnsure } from "./FormUnsure";
import { ReviewScreen, type ReviewTransport } from "./ReviewScreen";

export const TAKEN_OVER_NOTICE = "Umriss als Stand 1 gespeichert.";

/** `?lesarten=1` on the schema route: the readings of the form again, from a chosen one (plan 4 §10.1). */
export const OTHER_READING_PARAM = "lesarten";

export function editorHrefFor(caseId: string, candidate?: number): string {
  const base = `/umriss?case=${encodeURIComponent(caseId)}&aus=lesung`;
  return candidate === undefined ? base : `${base}&kandidat=${candidate}`;
}

/**
 * „Diese Lesart nehmen — Maße lesen“ (plan 4 §9) against `extractionId`:
 * `position` is the place in the list that reading shows (§9.1).
 *
 * After a choice the reading is asked again before anything moves: the
 * newest job is then the one just queued, and the route sends it to the
 * progress — never the old answer from the cache, which would bounce
 * straight back here.
 */
function useChoose(caseId: string, extractionId: string): (position: number) => Promise<void> {
  const navigate = useNavigate();
  const client = useQueryClient();
  return async (position: number) => {
    const job = await chooseForm(caseId, { extractionId, candidate: position });
    await client.refetchQueries({ queryKey: extractionKey(caseId), exact: true });
    // The same choice read before (200, succeeded) is its review again;
    // anything else is on its way.
    navigate(job.status === "succeeded" ? `/vorgang/${caseId}/schema` : `/vorgang/${caseId}/einlesen`, {
      replace: true,
    });
  };
}

/** A Vorgang with its Stand has nothing to take over or choose — the way on is the board. */
function StandExists({ planningHref }: { planningHref: string }) {
  return (
    <StateMessage
      title="Für diesen Vorgang gibt es schon einen Stand."
      action={
        <Link className="inline-link" to={planningHref}>
          Zur Planung
        </Link>
      }
    >
      Die Lesung ist übernommen. Weiter geht es in der Planung.
    </StateMessage>
  );
}

/**
 * A v2 reading of one Vorgang, on its schema route (plan 4 §2, step 3).
 *
 * The server truth around the review: whether the Vorgang has its Stand
 * already (then there is nothing to take over — the way on is the board),
 * whether the form is drawn — checked by the machine or chosen by a person
 * (§10.1) — or still to be chosen, and the transports: `take-over` dry after
 * every figure and for real on „Übernehmen und planen“; `choose-form` for
 * „Diese Lesart nehmen — Maße lesen“ (§9).
 */
export function CaseReview({
  caseId,
  review,
  source = null,
  onReload,
}: {
  caseId: string;
  review: ReadingReviewOut;
  /**
   * What the reading read (`ExtractionOut.source`). On a page of a longer
   * original, a form not made out offers „Andere Seite wählen“ — perhaps
   * the page was the wrong one (Seitenwahl, Folgepunkt a).
   */
  source?: ExtractionOut["source"] | null;
  onReload: () => void;
}) {
  useDocumentTitle("Schema");
  const navigate = useNavigate();
  const client = useQueryClient();
  const [params] = useSearchParams();
  const detail = useCase(caseId);
  const standing = (detail.data?.latestVersionNo ?? 0) > 0;
  const extractionId = review.extractionId;
  const planningHref = `/vorgang/${caseId}/planung`;
  const schemaHref = `/vorgang/${caseId}/schema`;
  const choose = useChoose(caseId, extractionId);

  const transport = useMemo<ReviewTransport>(
    () => ({
      dryRun: (resolutions: Resolution[]) =>
        takeOver(caseId, { extractionId, expectedVersionNo: 0, resolutions, dryRun: true }),
      takeOver: async (resolutions: Resolution[]) => {
        await takeOver(caseId, { extractionId, expectedVersionNo: 0, resolutions, dryRun: false });
      },
    }),
    [caseId, extractionId],
  );

  const reload = () => {
    void detail.refetch();
    onReload();
  };

  if (standing) return <StandExists planningHref={planningHref} />;

  const drawn = review.formStatus === "geprueft" || review.formStatus === "gewaehlt";
  const choosing = !drawn || (review.formStatus === "gewaehlt" && params.get(OTHER_READING_PARAM) === "1");

  if (choosing) {
    const original = source !== null ? originalOf(source, detail.data?.documents) : null;
    return (
      <FormUnsure
        key={`${extractionId}-choose`}
        review={review}
        pageUrl={extractionPageUrl(caseId, 1)}
        onChoose={choose}
        editorHref={(position) => editorHrefFor(caseId, position)}
        drawHref={`/umriss?case=${encodeURIComponent(caseId)}`}
        planningHref={planningHref}
        onReload={reload}
        backHref={review.formStatus === "gewaehlt" ? schemaHref : null}
        // Only where the original is on file: its pages are what is chosen.
        otherPageHref={original !== null ? otherPageHref(caseId) : null}
        readFrom={source !== null ? readFromLabel(source, original) : null}
        sheetLabel={source?.originalPage != null ? `Seite ${source.originalPage}` : "Seite 1"}
      />
    );
  }

  return (
    <ReviewScreen
      key={extractionId}
      caseId={caseId}
      review={review}
      pageUrl={extractionPageUrl(caseId, 1)}
      transport={transport}
      editorHref={editorHrefFor(caseId)}
      planningHref={planningHref}
      otherReadingHref={review.formStatus === "gewaehlt" ? `${schemaHref}?${OTHER_READING_PARAM}=1` : null}
      onTakenOver={() => {
        // Stand 1 now stands — in the cache before the board's frame reads it (R-6).
        noteStandWritten(client, caseId, 1);
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        void client.invalidateQueries({ queryKey: CASES_KEY });
        navigate(planningHref, { state: { notice: TAKEN_OVER_NOTICE } });
      }}
      onReload={reload}
    />
  );
}

/**
 * „Andere Lesart“ after the measures of a chosen reading failed (review
 * I-1): the readings of the form it was chosen from, as the server names
 * them on the failed job (`formChoice`) — in every tab and on every device.
 * There is no review to go back to; the readings stand in the order of the
 * reading of the sheet, and a choice names that reading. Whether it is still
 * the newest of the sheet the server judges (`reading_stale`).
 */
export function CaseFormChoice({
  caseId,
  choice,
  onReload,
}: {
  caseId: string;
  choice: FormChoice;
  onReload: () => void;
}) {
  useDocumentTitle("Schema");
  const detail = useCase(caseId);
  const planningHref = `/vorgang/${caseId}/planung`;
  const choose = useChoose(caseId, choice.baseExtractionId);

  const reload = () => {
    void detail.refetch();
    onReload();
  };

  if ((detail.data?.latestVersionNo ?? 0) > 0) return <StandExists planningHref={planningHref} />;

  return (
    <FormUnsure
      key={`${choice.baseExtractionId}-choose`}
      review={choice}
      pageUrl={extractionPageUrl(caseId, 1)}
      onChoose={choose}
      editorHref={(position) => editorHrefFor(caseId, position)}
      drawHref={`/umriss?case=${encodeURIComponent(caseId)}`}
      planningHref={planningHref}
      onReload={reload}
    />
  );
}
